/**
 * The frontend half of a plug-in module (ARCHITECT §19). A module package exports one manifest:
 *
 *   export const vendorsModule = defineModule({
 *     name: "vendors",                       // must match the backend module name
 *     requires: ["core", "accounts"],
 *     i18n: { en },                          // namespace = module name
 *     admin: { routes: [...], nav: [...] },
 *     vendor: { routes: [...], nav: [...] },
 *   });
 *
 * Apps list the modules they bundle in `src/modules.ts`. A module's routes and menu entries only
 * appear when the server reports it as installed (GET /api/v1/<panel>/modules) and the user has
 * the required permission.
 */

import type { ComponentType } from "react";

import type { IconName } from "@motolynk/ui";

export type Panel = "admin" | "vendor" | "store";

export interface ModuleRoute {
  /** Path relative to the app root, e.g. "vendors" or "vendors/:uid". */
  path: string;
  /** Code-split page: `() => import("./admin/VendorListPage")` (default export). */
  page: () => Promise<{ default: ComponentType }>;
  /**
   * - "panel" (default): inside the sidebar layout, login required
   * - "auth": public page in the login layout (login, forgot password…)
   * - "bare": login required, full screen without the sidebar (e.g. forced 2FA setup)
   */
  layout?: "panel" | "auth" | "bare";
  /** Django permission needed to open the page ("app_label.codename"). Admin panel only. */
  permission?: string;
  /** For auth pages: redirect logged-in users away (e.g. the login page). */
  guestOnly?: boolean;
  /** Boolean feature flag that must be on, e.g. "support.tickets" (otherwise 404). */
  feature?: string;
}

export interface ModuleNavItem {
  id: string;
  /** Group id from the app's navGroups (e.g. "marketplace"); "main" = top, ungrouped. */
  group: string;
  /** i18n key, e.g. "vendors:nav.vendors". */
  label: string;
  icon: IconName;
  to: string;
  permission?: string;
  /** Boolean feature flag that must be on for the entry to show. */
  feature?: string;
  order?: number;
}

export interface UserMenuEntry {
  id: string;
  /** i18n key */
  label: string;
  icon: IconName;
  to: string;
}

export interface TopbarWidget {
  id: string;
  /** Rendered in the top bar, left of the user menu (e.g. notifications → the bell). */
  component: ComponentType;
  /** Lower comes first (default 100). */
  order?: number;
}

/**
 * A piece of UI one module adds to another module's page, e.g. the orders module adds an "Orders" tab to the
 * customer page: `slots: { "customer.tabs": [{ id: "orders", label: "orders:customerTab", component: … }] }`.
 * The page renders `useSlot("customer.tabs")` and passes its props (here `{ customerUid }`).
 */
export interface SlotEntry<Props = Record<string, unknown>> {
  id: string;
  /** i18n key (tabs, headings). */
  label?: string;
  /** Shown only with this permission. */
  permission?: string;
  /** Lower comes first (default 100). */
  order?: number;
  component: ComponentType<Props>;
}

export interface PanelSpec {
  routes?: ModuleRoute[];
  nav?: ModuleNavItem[];
  /** Entries in the top-right user menu (e.g. accounts → "My profile"). */
  userMenu?: UserMenuEntry[];
  /** Widgets in the top bar (e.g. notifications → the bell). */
  topbar?: TopbarWidget[];
  /** UI added to other modules' pages, by slot name (see `SlotEntry`). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- each slot defines its own props
  slots?: Record<string, SlotEntry<any>[]>;
}

export interface ModuleDefinition {
  name: string;
  label: string;
  requires?: string[];
  /** { en: {...strings} } — registered under the module's name as i18n namespace. */
  i18n?: Record<string, Record<string, unknown>>;
  admin?: PanelSpec;
  vendor?: PanelSpec;
  store?: PanelSpec;
}

export function defineModule(definition: ModuleDefinition): ModuleDefinition {
  return definition;
}

/** Same rules as the backend: every `requires` entry is bundled and listed earlier. */
export function validateModules(modules: ModuleDefinition[]): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const module of modules) {
    if (seen.has(module.name)) problems.push(`Module "${module.name}" is listed twice.`);
    for (const dependency of module.requires ?? []) {
      if (!seen.has(dependency)) {
        problems.push(
          `Module "${module.name}" requires "${dependency}", which must be listed before it.`,
        );
      }
    }
    seen.add(module.name);
  }
  return problems;
}
